import React, { useEffect, useState } from "react";
import { styled } from "../stitches.config";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { body, bodyLarge, h4Strong } from "./commonStyles/Fonts";
import { useScrollBlock } from "../helpers/useScrollBlock";
import Media from "../components/common/Media";

interface GalleryProps {
  pictures: any[];
}

const Gallery: React.FC<GalleryProps> = ({ pictures }) => {
  const [isOpenModal, setOpenModal] = useState(false);
  const [blockScroll, allowScroll] = useScrollBlock();
  const toggleModal = () => {
    setOpenModal((prev) => !prev);
  };

  useEffect(() => {
    isOpenModal ? blockScroll() : allowScroll();

    return () => allowScroll();
  }, [isOpenModal, allowScroll, blockScroll]);

  const settings = {
    customPaging: function (i: number) {
      if (i === 4 && pictures.length > 5) {
        return (
          <a>
            <Mask onClick={toggleModal} className={h4Strong()}>
              +{pictures.length - i - 1}{" "}
            </Mask>
            <Media
              height={64}
              width={120}
              media={pictures[i].data.media}
              src={pictures[i].data.img}
              preserveRatio={false}
              aspectRatio={1.875}
            />
          </a>
        );
      }
      if (i <= 4)
        return (
          <a>
            <Media
              height={64}
              width={120}
              media={pictures[i].data.media}
              src={pictures[i].data.img}
              preserveRatio={false}
              aspectRatio={1.875}
            />
          </a>
        );

      return <div />;
    },
    dots: true,
    dotsClass: "slick-dots slick-thumb",
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipe: false,
    arrows: false,
  };
  const settingsMobile = {
    dots: true,
    arrows: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipe: true,
  };
  return (
    <Wrapper>
      <OnlyDesktop>
        <Slider {...settings}>
          {pictures.map(({ title, data }) => (
            <MediaWrapper key={title}>
              <Media
                height={700}
                media={data.media}
                src={data.img}
                preserveRatio={false}
                aspectRatio={1.5}
              />
            </MediaWrapper>
          ))}
        </Slider>
      </OnlyDesktop>
      <OnlyMobile>
        <Slider {...settingsMobile}>
          {pictures.map(({ title, data }) => (
            <MediaWrapper key={title}>
              <Media
                height={235}
                media={data.media}
                src={data.img}
                preserveRatio={false}
                aspectRatio={1.5}
              />
            </MediaWrapper>
          ))}
        </Slider>
      </OnlyMobile>
      <GalleryButton onClick={toggleModal} className={bodyLarge()}>
        Zobacz galerię
      </GalleryButton>
      {isOpenModal && (
        <Modal>
          <CloseButton onClick={toggleModal}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M13.753 12.4147L21.5459 4.62168L19.7075 2.7832L11.9145 10.5762L4.12168 2.78336L2.2832 4.62183L10.076 12.4147L2.2832 20.2075L4.12168 22.0459L11.9145 14.2531L19.7075 22.0461L21.5459 20.2076L13.753 12.4147ZM11.9145 11.0004L19.7075 3.20747L19.7075 3.20752L11.9146 11.0005L11.9145 11.0004ZM4.12168 3.20762L4.12173 3.20767L2.70752 4.62189L2.70747 4.62183L4.12168 3.20762ZM10.5003 12.4147L2.70747 20.2075L2.70752 20.2075L10.5003 12.4147L10.5003 12.4147Z" />
            </svg>
          </CloseButton>
          {pictures.map(({ title, alt, data, figcaption }, index) => (
            <div>
              <ImageWrapper>
                <Media
                  src={data.img}
                  alt={alt}
                  height={data.height}
                  width={data.width}
                  media={data.media}
                />
              </ImageWrapper>
              <span>
                {index + 1}/{pictures.length}
              </span>
              <h5 className={body()}>{title}</h5>
              <p className={body()}>{figcaption}</p>
            </div>
          ))}
        </Modal>
      )}
    </Wrapper>
  );
};

export default Gallery;

const Wrapper = styled("div", {
  position: "relative",
  marginBottom: "120px",
  ".slick-thumb": {
    top: "calc(100% + 20px)",
    li: {
      width: "auto",
      height: "auto",
      img: {
        maxHeight: "64px",
        maxWidth: "120px",
        margin: "0 0 1.45rem",
        padding: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
      },
      "&.slick-active": {
        position: "relative",
        "&::after": {
          content: "",
          position: "absolute",
          top: "-4px",
          left: "-4px",
          height: "calc(100% + 8px)",
          width: "calc(100% + 8px)",
          border: "2px solid black",
        },
      },
    },
    height: "64px",
  },
  ".slick-dots": {
    position: "absolute",
    bottom: "-25px",
    width: "100%",
    padding: 0,
    margin: 0,
    listStyle: "none",
    textAlign: "center",
    li: {
      button: {
        "&::before": {
          color: "$grayscale50",
          opacity: 1,
        },
      },
    },
    "li.slick-active": {
      button: {
        "&::before": {
          color: "$natematPrimary",
          opacity: 1,
        },
      },
    },
  },
  ".slick-track": {
    height: "224px",
    "& *": {
      height: "100%",
    },
  },
  ".slick-slide": {
    "& > div > div": {
      display: "flex !important",
      alignItems: "center",
      justifyContent: "center",
    },
  },
  img: {
    maxWidth: "100%",
    objectFit: "contain",
  },
  "@bp4": {
    marginBottom: "100px",
    ".slick-track": {
      height: "472px",
    },
    ".slick-dots": {
      textAlign: "left",
      display: "flex !important",
      transform: "translateX(-5px)",
    },
  },
});

const Mask = styled("div", {
  height: "100%",
  width: "100%",
  position: "absolute",
  top: 0,
  left: 0,
  background: "rgba(19, 18, 18, 0.4)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "white",
});

const GalleryButton = styled("button", {
  background: "$grayscale100",
  color: "$grayscale0",
  border: "none",
  position: "absolute",
  top: "calc(100% + 40px)",
  right: 0,
  width: "100%",
  padding: "12px 0",
  transition: "0.3s",
  "&:hover": {
    color: "$primary",
  },
  "@bp4": {
    top: "calc(100% + 20px)",
    width: "auto",
    padding: "20px",
  },
});

const Modal = styled("div", {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100%",
  minHeight: "100vh",
  backgroundColor: "$grayscale0",
  zIndex: 5000000,
  overflow: "auto",
  padding: "16px 16px 72px 16px",
  img: {
    width: "100%",
    paddingLeft: "0 !important",
  },
  h5: {
    margin: "6px 0",
  },
  p: {
    color: "$grayscale75",
    margin: 0,
  },
  span: {
    display: "block",
    fontFamily: "CharisSIL",
    fontWeight: 700,
    lineHeight: "26px",
    marginTop: "16px",
  },
  "@bp4": {
    padding: "60px 84px 84px 84px",
    textAlign: "center",
    img: {
      //maxHeight: "716px",
      width: "auto",
    },
  },
});

const CloseButton = styled("button", {
  top: "27px",
  right: "16px",
  position: "fixed",
  display: "block",
  flexShrink: 0,
  border: "none",
  background: "none",
  width: "24px !important",
  height: "24px !important",
  padding: "0 !important",
  zIndex: 4,

  "@bp4": {
    top: "48px",
    right: "84px",
  },

  "svg": {
    fill: "$primary",
    width: "24px",
    height: "24px",
  }
});

const OnlyDesktop = styled("div", {
  display: "none",
  "@bp4": {
    display: "block",
  },
});

const OnlyMobile = styled("div", {
  "@bp4": {
    display: "none",
  },
});

const MediaWrapper = styled("div", {
  div: {
    width: "100%",
  },
});

const ImageWrapper = styled("div", {
  position: "relative",
  width: "100%",
  flexShrink: "0",
  marginTop: "60px",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
});
